import type { FUSportsMatchEvent, FUSportsFootballEventType } from "fansunited-data-layer";
import type { Labels } from "@/lib/labels/types";

export type EventLabels = Labels["matchPage"]["events"];

/**
 * Key event types to display in the match timeline
 */
export const KEY_EVENT_TYPES: FUSportsFootballEventType[] = [
    "GOAL",
    "PENALTY_GOAL",
    "OWN_GOAL",
    "YELLOW_CARD",
    "RED_CARD",
    "YELLOW_RED_CARD",
    "SUBSTITUTION",
    "PENALTY_MISSED",
    "PENALTY_SAVED",
];

/**
 * Get event type label
 */
export function getEventTypeLabel(
    type: FUSportsFootballEventType | string,
    eventLabels: EventLabels
): string {
    switch (type) {
        case "GOAL":
            return eventLabels.goal;
        case "PENALTY_GOAL":
            return eventLabels.penalty;
        case "PENALTY_SHOOTOUT_SCORED":
            return eventLabels.penaltyScored;
        case "OWN_GOAL":
            return eventLabels.ownGoal;
        case "YELLOW_CARD":
            return eventLabels.yellowCard;
        case "RED_CARD":
            return eventLabels.redCard;
        case "YELLOW_RED_CARD":
            return eventLabels.secondYellow;
        case "SUBSTITUTION":
            return eventLabels.substitution;
        case "PENALTY_MISSED":
        case "PENALTY_SAVED":
            return eventLabels.penaltyMissed;
        case "PENALTY_SHOOTOUT_MISSED":
            return eventLabels.penaltyMissed;
        case "PENALTY_AWARDED":
            return eventLabels.penaltyAwarded;
        default:
            return eventLabels.event;
    }
}

/**
 * Format minute display
 */
export function formatMinute(event: FUSportsMatchEvent): string {
    const minute = event.minute || 0;
    const injury = event.injuryMinute;
    return injury ? `${minute}+${injury}'` : `${minute}'`;
}

/**
 * Check if event is a goal type
 */
export function isGoalEvent(type: FUSportsFootballEventType | string): boolean {
    return ["GOAL", "PENALTY_GOAL", "OWN_GOAL", "PENALTY_SHOOTOUT_SCORED"].includes(type);
}

/**
 * Get effective minute for sorting (includes injury time as decimal)
 */
export function getEffectiveMinute(event: FUSportsMatchEvent): number {
    return (event.minute || 0) + (event.injuryMinute || 0) * 0.01;
}

/**
 * Get player photo URL from player assets
 */
export function getPlayerPhoto(player?: { assets?: { photoTransparent?: string; photo?: string } }): string | undefined {
    return player?.assets?.photoTransparent || player?.assets?.photo;
}

